export type KdsNotificationSound = 'none' | 'test_message';

type AudioMime = 'audio/wav';

type SoundSource = {
  src: string;
  mime: AudioMime;
};

type AudioLike = Pick<HTMLAudioElement, 'canPlayType' | 'play' | 'preload' | 'currentTime' | 'pause'>;

export type AudioFactory = (src: string) => AudioLike;

const SOUND_SOURCE_MAP: Record<KdsNotificationSound, ReadonlyArray<SoundSource>> = {
  none: [],
  test_message: [
    { src: '/sounds/kds/ding.wav', mime: 'audio/wav' },
  ],
};

const audioInstanceBySrc = new Map<string, AudioLike>();

function getOrCreateAudio(src: string, createAudio: AudioFactory): AudioLike {
  const cached = audioInstanceBySrc.get(src);
  if (cached) {
    return cached;
  }
  const created = createAudio(src);
  audioInstanceBySrc.set(src, created);
  return created;
}

export function resetKdsNotificationAudioCacheForTests(): void {
  audioInstanceBySrc.clear();
}

// Architectural summary: Converts the configured KDS sound key into WAV source list.
// We intentionally use WAV-only playback for predictable behavior on KDS devices.
export function listSoundSources(sound: KdsNotificationSound): ReadonlyArray<SoundSource> {
  return SOUND_SOURCE_MAP[sound] ?? [];
}

// Architectural summary: Returns only the sources that the current browser reports as playable
// via HTMLAudioElement.canPlayType(). This avoids attempting obvious non-supported codecs and
// reduces noisy play() rejections when users switch browsers/devices.
export function filterPlayableSources(
  sources: ReadonlyArray<SoundSource>,
  createAudio: AudioFactory = (src) => new Audio(src),
): ReadonlyArray<SoundSource> {
  const probe = createAudio('');
  return sources.filter((source) => probe.canPlayType(source.mime) !== '');
}

// Architectural summary: Primes the notification sound from an explicit user gesture so iOS
// and other mobile browsers treat later notification playback as user-authorized media.
export async function primeKdsNotificationSound(
  sound: KdsNotificationSound,
  createAudio: AudioFactory = (src) => new Audio(src),
): Promise<boolean> {
  const source = filterPlayableSources(listSoundSources(sound), createAudio)[0];
  if (!source) {
    return false;
  }

  const audio = getOrCreateAudio(source.src, createAudio);
  audio.preload = 'auto';
  audio.currentTime = 0;
  try {
    await audio.play();
    audio.pause();
    audio.currentTime = 0;
    return true;
  } catch {
    return false;
  }
}

// Architectural summary: Plays the configured KDS notification sound using WAV source(s).
// On each notification trigger, we try WAV playback and return whether it succeeded.
export async function playKdsNotificationSound(
  sound: KdsNotificationSound,
  createAudio: AudioFactory = (src) => new Audio(src),
): Promise<boolean> {
  const sources = filterPlayableSources(listSoundSources(sound), createAudio);
  for (const source of sources) {
    const audio = getOrCreateAudio(source.src, createAudio);
    audio.preload = 'auto';
    audio.currentTime = 0;
    try {
      await audio.play();
      return true;
    } catch {
      // Try next format source.
    }
  }
  return false;
}
